import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Users, Building2 } from "lucide-react";

export default function TeamJoinPage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/team-invite/${token}`).then((r) => {
      setInvite(r.data.invite);
      setTenant(r.data.tenant);
    }).catch((e) => setErr(fmtErr(e.response?.data?.detail) || "Invite invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/team-invite/${token}/accept`, form);
      // Drop any stale session that might be in this browser (e.g. the
      // platform owner was logged in on the same device) and hard-reload so
      // AuthProvider re-initialises from scratch with the new token.
      localStorage.removeItem("tp_tenant_cache");
      if (data.token) localStorage.setItem("tp_token", data.token);
      toast.success(data.new_workspace ? `Welcome! Your workspace "${data.workspace_name}" is ready.` : "Welcome to the team!");
      window.location.href = "/app";
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); setBusy(false); }
  };

  if (err) return <div className="app-light min-h-screen grid place-items-center p-6"><div className="tp-card p-8 max-w-md text-center"><div className="font-display text-xl mb-2">Invite invalid</div><div className="text-slate-400 text-sm">{err}</div></div></div>;
  if (!invite) return <div className="app-light min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  const isNewWorkspace = !!tenant?.is_new_workspace;
  return (
    <div className="app-light min-h-screen flex items-center justify-center p-6" data-testid="team-join">
      <form onSubmit={submit} className="w-full max-w-md tp-card p-8">
        <div className="flex items-center gap-2 mb-6">
          {isNewWorkspace ? (
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center"><Building2 className="w-5 h-5 text-white"/></div>
          ) : tenant?.logo_data_url ? (
            <img src={tenant.logo_data_url} alt={tenant.name} className="h-8 max-w-[120px] object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center"><Sparkles className="w-4 h-4 text-white"/></div>
          )}
          <div className="font-display font-bold text-lg">{tenant?.name || "RecruitSift"}</div>
        </div>
        {isNewWorkspace && (
          <div className="tp-chip tp-chip-emerald mb-4" data-testid="new-workspace-badge"><Building2 className="w-3 h-3 mr-1 inline"/> New workspace</div>
        )}
        <div className="tp-chip tp-chip-indigo mb-4"><Users className="w-3 h-3 mr-1 inline"/> {invite.role} invitation</div>
        <h1 className="font-display text-2xl font-semibold mb-1">{isNewWorkspace ? `Set up ${tenant.name}` : `Join ${tenant?.name || "the team"}`}</h1>
        <p className="text-sm text-slate-500 mb-6">
          {isNewWorkspace
            ? <>You're being onboarded as the admin of <b>{tenant.name}</b> — your own private workspace. Create a password to activate it.</>
            : <>You've been invited as a <b>{invite.role}</b>. Create a password to activate your account.</>}
        </p>
        <label className="text-xs text-slate-400 uppercase tracking-wider">Email</label>
        <input value={invite.email} disabled className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] text-slate-500" />
        <label className="text-xs text-slate-400 uppercase tracking-wider">Your Name</label>
        <input data-testid="tj-name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
        <label className="text-xs text-slate-400 uppercase tracking-wider">Password</label>
        <input data-testid="tj-password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} type="password" required minLength={6} className="w-full mt-1 mb-6 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
        <button data-testid="tj-submit" disabled={busy} className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white font-medium">{busy ? "Setting up…" : isNewWorkspace ? "Create workspace & join" : "Accept & Join"}</button>
      </form>
    </div>
  );
}
